import { Box } from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import { DocIcon } from './DocIcon';
import { EntryListItem } from './EntryListItem';
import { NavToggleButton } from './NavToggleButton';
import { getDoneSlugs } from './useCourseProgress';

const cardSx = {
  border: '1px solid var(--border)',
  background: 'var(--panel)',
  borderRadius: '12px',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: 'var(--text)',
  ':hover': { borderColor: 'var(--accent-border)', boxShadow: 'var(--shadow)' },
};

const sectionLabelSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: 'var(--text2)',
  marginBottom: '12px',
};

const iconTileSx = (size) => ({
  width: `${size}px`,
  height: `${size}px`,
  flex: 'none',
  borderRadius: `${Math.round(size / 3.8)}px`,
  background: 'var(--accent-soft)',
  color: 'var(--accent-strong)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Documentation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale, search, searchInput, setSearchInput, setNavOpen, topics, entries, courses } =
    useOutletContext();

  const { apiData: resultsData, isLoading: resultsLoading } = useFetch(
    search
      ? `documentation?locale=${locale}&search=${encodeURIComponent(search)}`
      : null
  );

  const visibleTopics = topics?.filter((topic) => topic.entriesCount > 0);
  const popular = entries?.filter((entry) => entry.isPopular);

  const openEntry = (slug) => navigate(`/documentation/articles/${slug}`);

  function openTopic(topic) {
    // The design has no topic page — a topic card opens its first entry
    const first = entries?.find((entry) =>
      entry.topics?.some((item) => item.slug === topic.slug)
    );
    if (first) openEntry(first.slug);
  }

  return (
    <Box component="main" className="flex-1 min-w-0 h-full overflow-y-auto">
      <Box className="mx-auto" sx={{ maxWidth: '920px', padding: '28px 40px 80px' }}>
        <Box className="flex items-center gap-2">
          <NavToggleButton onClick={() => setNavOpen(true)} />
        </Box>

        <Box className="text-center" sx={{ padding: '44px 0 36px' }}>
          <Box component="h1" sx={{ fontSize: '32px', fontWeight: 700, margin: '0 0 10px' }}>
            {t('How can we help?')}
          </Box>
          <Box component="p" sx={{ fontSize: '14px', color: 'var(--text2)', margin: '0 0 26px' }}>
            {t('Guides, videos and courses for ExoPaper.')}
          </Box>
          <Box
            component="label"
            className="flex items-center cursor-text mx-auto"
            sx={{
              gap: '10px',
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '14px 18px',
              maxWidth: '560px',
              boxShadow: 'var(--shadow)',
            }}
          >
            <DocIcon name="search" size={20} color="var(--text2)" />
            <Box
              component="input"
              placeholder={t('Search term, e.g. “create invoice” …')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-0 border-none outline-none bg-transparent"
              sx={{ color: 'var(--text)', fontFamily: 'inherit', fontSize: '14px' }}
            />
            <Box
              component="kbd"
              sx={{
                fontFamily: 'inherit',
                fontSize: '10px',
                fontWeight: 600,
                color: 'var(--text2)',
                border: '1px solid var(--border)',
                borderRadius: '5px',
                padding: '2px 6px',
                background: 'var(--panel2)',
              }}
            >
              ⌘ K
            </Box>
          </Box>
        </Box>

        {search ? (
          <Box>
            <Box sx={sectionLabelSx}>{t('Search results')}</Box>
            {resultsLoading || !resultsData ? (
              <Box className="flex flex-col" sx={{ gap: '6px' }}>
                <ExoSkeleton n={6} size="36px" borderRadius="8px" />
              </Box>
            ) : resultsData.data.length ? (
              <Box className="flex flex-col" sx={{ gap: '6px' }}>
                {resultsData.data.map((entry) => (
                  <EntryListItem
                    key={entry.slug}
                    entry={entry}
                    onClick={() => openEntry(entry.slug)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ color: 'var(--text2)', fontSize: '13px' }}>
                {t('No documentation found')}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box
              className="grid"
              sx={{
                gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))',
                gap: '14px',
              }}
            >
              {!visibleTopics && (
                <ExoSkeleton n={6} size="152px" borderRadius="12px" />
              )}
              {visibleTopics?.map((topic) => (
                <Box
                  key={topic.slug}
                  component="button"
                  onClick={() => openTopic(topic)}
                  className="flex flex-col"
                  sx={{ ...cardSx, padding: '18px', gap: '10px' }}
                >
                  <Box component="span" sx={iconTileSx(38)}>
                    <DocIcon name={topic.icon} size={21} />
                  </Box>
                  <Box component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {topic.name}
                  </Box>
                  {topic.description && (
                    <Box
                      component="span"
                      sx={{ fontSize: '12px', color: 'var(--text2)', lineHeight: 1.5 }}
                    >
                      {topic.description}
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 600 }}
                  >
                    {t('{{n}} entries', { n: topic.entriesCount })}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              className="grid grid-cols-1 md:grid-cols-2"
              sx={{ gap: '28px', marginTop: '40px' }}
            >
              <Box>
                <Box sx={sectionLabelSx}>{t('Frequently viewed')}</Box>
                <Box className="flex flex-col" sx={{ gap: '6px' }}>
                  {!popular && <ExoSkeleton n={5} size="36px" borderRadius="8px" />}
                  {popular?.map((entry) => (
                    <EntryListItem
                      key={entry.slug}
                      entry={entry}
                      onClick={() => openEntry(entry.slug)}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Box sx={sectionLabelSx}>{t('Guided courses')}</Box>
                <Box className="flex flex-col" sx={{ gap: '10px' }}>
                  {!courses && <ExoSkeleton n={3} size="62px" borderRadius="11px" />}
                  {courses?.map((course) => {
                    const doneCount = getDoneSlugs(course.slug).length;
                    return (
                      <Box
                        key={course.slug}
                        component="button"
                        onClick={() =>
                          navigate(`/documentation/courses/${course.slug}`)
                        }
                        className="flex items-center"
                        sx={{
                          ...cardSx,
                          gap: '12px',
                          borderRadius: '11px',
                          padding: '13px 15px',
                        }}
                      >
                        <Box component="span" sx={iconTileSx(34)}>
                          <DocIcon name={course.icon || 'school'} size={19} />
                        </Box>
                        <Box component="span" className="flex-1 min-w-0">
                          <Box
                            component="span"
                            className="block"
                            sx={{ fontSize: '13px', fontWeight: 600 }}
                          >
                            {course.title}
                          </Box>
                          <Box
                            component="span"
                            className="block"
                            sx={{
                              fontSize: '11px',
                              color: 'var(--text2)',
                              marginTop: '2px',
                            }}
                          >
                            {t('{{lessons}} lessons · {{minutes}} min.', {
                              lessons: course.lessonsCount,
                              minutes: course.totalMinutes,
                            })}
                          </Box>
                        </Box>
                        {doneCount > 0 && (
                          <Box
                            component="span"
                            className="whitespace-nowrap"
                            sx={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: 'var(--accent-strong)',
                            }}
                          >
                            {doneCount} / {course.lessonsCount}
                          </Box>
                        )}
                        <DocIcon name="chevron_right" size={18} color="var(--text2)" />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Box
              className="flex items-center justify-between flex-wrap"
              sx={{
                gap: '16px',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                background: 'var(--panel)',
                padding: '18px 22px',
                marginTop: '40px',
              }}
            >
              <Box className="flex items-center" sx={{ gap: '12px' }}>
                <DocIcon name="support_agent" size={22} color="var(--accent)" />
                <Box>
                  <Box sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {t('Didn’t find what you were looking for?')}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: 'var(--text2)', marginTop: '2px' }}>
                    {t('Our support responds within 24 hours on weekdays.')}
                  </Box>
                </Box>
              </Box>
              <Box
                component="a"
                href="mailto:support@exopaper.de"
                className="cursor-pointer"
                sx={{
                  border: '1px solid var(--accent-border)',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-strong) !important',
                  fontFamily: 'inherit',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  borderRadius: '9px',
                  padding: '9px 18px',
                  textDecoration: 'none !important',
                  ':hover': { filter: 'brightness(1.05)' },
                }}
              >
                {t('Contact support')}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Documentation;
